import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Building2,
  ExternalLink,
  Image as ImageIcon,
  Loader2,
  Trash2,
  Upload,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Button } from "./ui/button";
import { toast } from "sonner";

const MAX_SOURCE_FILE_BYTES = 10 * 1024 * 1024;

const IMAGE_CONFIG = {
  logo: {
    maxWidth: 640,
    maxHeight: 640,
    targetBytes: 120 * 1024,
    initialQuality: 0.82,
  },
  hero: {
    maxWidth: 1600,
    maxHeight: 800,
    targetBytes: 320 * 1024,
    initialQuality: 0.8,
  },
};

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];

function approximateDataUrlBytes(dataUrl) {
  if (!dataUrl) return 0;

  const comma = dataUrl.indexOf(",");
  if (comma === -1) return 0;

  return Math.ceil((dataUrl.slice(comma + 1).length * 3) / 4);
}

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new window.Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("The selected image could not be opened."));
    };

    image.src = objectUrl;
  });
}

async function compressImage(file, type) {
  const config = IMAGE_CONFIG[type];

  if (!config) throw new Error("Unknown image type.");

  if (!ACCEPTED_TYPES.includes(file.type)) {
    throw new Error("Please upload a JPG, PNG or WebP image.");
  }

  if (file.size > MAX_SOURCE_FILE_BYTES) {
    throw new Error("Please choose an image smaller than 10 MB.");
  }

  const image = await loadImageFromFile(file);

  let width = image.naturalWidth || image.width;
  let height = image.naturalHeight || image.height;

  if (!width || !height) {
    throw new Error("The selected image has invalid dimensions.");
  }

  const initialScale = Math.min(
    1,
    config.maxWidth / width,
    config.maxHeight / height
  );

  width = Math.max(1, Math.round(width * initialScale));
  height = Math.max(1, Math.round(height * initialScale));

  let quality = config.initialQuality;
  let bestDataUrl = null;

  for (let attempt = 0; attempt < 12; attempt += 1) {
    const canvas = document.createElement("canvas");

    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d", {
      alpha: true,
    });

    if (!context) {
      throw new Error("Your browser could not process this image.");
    }

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";

    context.drawImage(image, 0, 0, width, height);

    const dataUrl = canvas.toDataURL("image/webp", quality);

    bestDataUrl = dataUrl;

    if (approximateDataUrlBytes(dataUrl) <= config.targetBytes) {
      return dataUrl;
    }

    if (quality > 0.58) {
      quality -= 0.07;
    } else {
      width = Math.max(320, Math.round(width * 0.86));

      height = Math.max(
        type === "hero" ? 180 : 320,
        Math.round(height * 0.86)
      );

      quality = 0.72;
    }
  }

  if (!bestDataUrl) {
    throw new Error("The image could not be processed.");
  }

  if (approximateDataUrlBytes(bestDataUrl) > 700 * 1024) {
    throw new Error(
      "The image is still too large after compression. Please choose a smaller image."
    );
  }

  return bestDataUrl;
}

function buildProfilePayload(
  profile,
  overrides = {},
  forcePublished = false
) {
  return {
    profile_tagline: profile?.profile_tagline || null,
    profile_about: profile?.profile_about || null,
    profile_logo_url: profile?.profile_logo_url || null,
    profile_hero_image_url:
      profile?.profile_hero_image_url || null,
    profile_website: profile?.profile_website || null,
    profile_careers_url:
      profile?.profile_careers_url || null,
    profile_published:
      forcePublished ||
      Boolean(profile?.profile_published),
    ...overrides,
  };
}

function setReactInputValue(input, value) {
  if (!input) return;

  const descriptor =
    Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      "value"
    );

  if (descriptor?.set) {
    descriptor.set.call(input, value);
  } else {
    input.value = value;
  }

  input.dispatchEvent(
    new Event("input", {
      bubbles: true,
    })
  );

  input.dispatchEvent(
    new Event("change", {
      bubbles: true,
    })
  );
}

function synchroniseLegacyCompanyEditor(
  profile,
  shouldPublish
) {
  const logoInput =
    document.querySelector(
      'input[data-testid="profile-logo"]'
    );

  const heroInput =
    document.querySelector(
      'input[data-testid="profile-hero"]'
    );

  const publishInput =
    document.querySelector(
      'input[data-testid="profile-publish-checkbox"]'
    );

  if (logoInput) {
    logoInput.type = "text";

    const wrapper =
      logoInput.parentElement;

    if (wrapper) {
      wrapper.style.display = "none";
    }

    if (
      logoInput.value !==
      (profile?.profile_logo_url || "")
    ) {
      setReactInputValue(
        logoInput,
        profile?.profile_logo_url || ""
      );
    }
  }

  if (heroInput) {
    heroInput.type = "text";

    const wrapper =
      heroInput.parentElement;

    if (wrapper) {
      wrapper.style.display = "none";
    }

    if (
      heroInput.value !==
      (profile?.profile_hero_image_url || "")
    ) {
      setReactInputValue(
        heroInput,
        profile?.profile_hero_image_url || ""
      );
    }
  }

  if (
    publishInput &&
    shouldPublish
  ) {
    const wrapper =
      publishInput.closest("label") ||
      publishInput.parentElement;

    if (wrapper) {
      wrapper.style.display = "none";
    }

    if (!publishInput.checked) {
      const checkedDescriptor =
        Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          "checked"
        );

      if (checkedDescriptor?.set) {
        checkedDescriptor.set.call(
          publishInput,
          true
        );
      } else {
        publishInput.checked = true;
      }

      publishInput.dispatchEvent(
        new Event("change", {
          bubbles: true,
        })
      );
    }
  }
}

function ImagePreview({
  src,
  type,
  companyName,
}) {
  if (!src) {
    return (
      <div
        className={
          type === "hero"
            ? "h-32 md:h-36 rounded-lg border border-dashed border-border bg-sand flex items-center justify-center"
            : "w-24 h-24 rounded-lg border border-dashed border-border bg-sand flex items-center justify-center"
        }
      >
        <ImageIcon className="w-7 h-7 text-muted-foreground" />
      </div>
    );
  }

  if (type === "hero") {
    return (
      <div className="h-32 md:h-36 rounded-lg overflow-hidden border border-border bg-sand">
        <img
          src={src}
          alt={`${companyName || "Company"} hero`}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  return (
    <div className="w-24 h-24 rounded-lg overflow-hidden border border-border bg-white flex items-center justify-center">
      <img
        src={src}
        alt={`${companyName || "Company"} logo`}
        className="w-full h-full object-contain p-2"
      />
    </div>
  );
}

export default function EmployerProfileImageUploader() {
  const location = useLocation();

  const { user } =
    useAuth();

  const onEmployerDashboard =
    location.pathname.startsWith(
      "/employer-portal/dashboard"
    );

  const isEmployer =
    user?.role === "employer" ||
    user?.role === "admin";

  const canPublish =
    user?.role === "admin" ||
    user?.approved === true;

  const active =
    onEmployerDashboard &&
    Boolean(user) &&
    isEmployer;

  const [profile, setProfile] =
    useState(null);

  const [loading, setLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(null);

  useEffect(() => {
    if (!active) {
      setProfile(null);
      return undefined;
    }

    let cancelled = false;

    const loadAndPublishProfile =
      async () => {
        setLoading(true);

        try {
          const response =
            await api.get(
              "/employer/profile"
            );

          let nextProfile =
            response.data || null;

          if (
            nextProfile &&
            canPublish &&
            !nextProfile.profile_published
          ) {
            const publishResponse =
              await api.put(
                "/employer/profile",
                buildProfilePayload(
                  nextProfile,
                  {},
                  true
                )
              );

            nextProfile =
              publishResponse.data;
          }

          if (!cancelled) {
            setProfile(nextProfile);
          }
        } catch {
          if (!cancelled) {
            setProfile(null);
          }
        } finally {
          if (!cancelled) {
            setLoading(false);
          }
        }
      };

    loadAndPublishProfile();

    return () => {
      cancelled = true;
    };
  }, [active, canPublish]);

  useEffect(() => {
    if (!active || !profile) {
      return undefined;
    }

    const sync = () =>
      synchroniseLegacyCompanyEditor(
        profile,
        canPublish
      );

    sync();

    const observer =
      new MutationObserver(sync);

    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true,
      }
    );

    return () =>
      observer.disconnect();
  }, [
    active,
    canPublish,
    profile,
  ]);

  if (!active) {
    return null;
  }

  const updateProfileImage =
    async (
      type,
      nextValue
    ) => {
      setSaving(type);

      try {
        const latestResponse =
          await api.get(
            "/employer/profile"
          );

        const latest =
          latestResponse.data ||
          profile ||
          {};

        const overrides =
          type === "logo"
            ? {
                profile_logo_url:
                  nextValue,
              }
            : {
                profile_hero_image_url:
                  nextValue,
              };

        const response =
          await api.put(
            "/employer/profile",
            buildProfilePayload(
              latest,
              overrides,
              canPublish
            )
          );

        setProfile(
          response.data
        );

        toast.success(
          type === "logo"
            ? "Company logo saved."
            : "Hero image saved."
        );
      } catch (error) {
        toast.error(
          error.response?.data
            ?.detail ||
            "The image could not be saved."
        );
      } finally {
        setSaving(null);
      }
    };

  const handleFile =
    async (
      event,
      type
    ) => {
      const file =
        event.target.files?.[0];

      event.target.value = "";

      if (!file) return;

      setSaving(type);

      try {
        const dataUrl =
          await compressImage(
            file,
            type
          );

        await updateProfileImage(
          type,
          dataUrl
        );
      } catch (error) {
        toast.error(
          error.message ||
            "The image could not be processed."
        );

        setSaving(null);
      }
    };

  const clearImage =
    async (type) => {
      const wording =
        type === "logo"
          ? "Remove the uploaded company logo and return to the automatic logo?"
          : "Remove the hero image?";

      if (
        !window.confirm(wording)
      ) {
        return;
      }

      await updateProfileImage(
        type,
        ""
      );
    };

  const companyName =
    profile?.company_name ||
    user?.company_name ||
    "Your company";

  return (
    <section
      className="bg-cream border-b border-border"
      data-testid="employer-profile-image-uploader"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 md:px-6 py-5 border-b border-border flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber/20 flex items-center justify-center shrink-0">
                <Building2 className="w-5 h-5 text-amber-700" />
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">
                  Company page
                </div>

                <h2 className="font-display font-bold text-ink text-xl mt-1">
                  Company images
                </h2>

                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                  Upload your company logo and an optional hero image.
                  Approved employer pages are published automatically on
                  Railway Careers UK.
                </p>
              </div>
            </div>

            {canPublish &&
              profile?.profile_slug && (
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                >
                  <Link
                    to={`/companies/${profile.profile_slug}`}
                    target="_blank"
                  >
                    View company page
                    <ExternalLink className="ml-2 w-3.5 h-3.5" />
                  </Link>
                </Button>
              )}
          </div>

          {loading ? (
            <div className="p-8 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading company page…
            </div>
          ) : !profile ? (
            <div className="p-6 text-sm text-muted-foreground">
              Company profile information is not available yet.
            </div>
          ) : (
            <div className="p-5 md:p-6 grid lg:grid-cols-2 gap-5">
              <div className="rounded-xl border border-border bg-cream/40 p-5">
                <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-ink">
                  Company logo
                </div>

                <p className="text-xs text-muted-foreground mt-1">
                  Best results: a square or landscape logo with a clear
                  background.
                </p>

                <div className="mt-4 flex items-center gap-5 flex-wrap">
                  <ImagePreview
                    src={
                      profile.profile_logo_url
                    }
                    type="logo"
                    companyName={
                      companyName
                    }
                  />

                  <div className="flex-1 min-w-[180px]">
                    <input
                      id="company-logo-upload"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(
                        event
                      ) =>
                        handleFile(
                          event,
                          "logo"
                        )
                      }
                    />

                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        className="bg-navy text-bone hover:bg-lime-600 hover:text-ink"
                        disabled={
                          saving ===
                          "logo"
                        }
                        onClick={() =>
                          document
                            .getElementById(
                              "company-logo-upload"
                            )
                            ?.click()
                        }
                      >
                        {saving ===
                        "logo" ? (
                          <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="mr-2 w-4 h-4" />
                        )}

                        {profile.profile_logo_url
                          ? "Replace logo"
                          : "Upload logo"}
                      </Button>

                      {profile.profile_logo_url && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={
                            saving ===
                            "logo"
                          }
                          onClick={() =>
                            clearImage(
                              "logo"
                            )
                          }
                        >
                          <Trash2 className="mr-2 w-3.5 h-3.5" />
                          Reset
                        </Button>
                      )}
                    </div>

                    <div className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
                      JPG, PNG or WebP. Maximum source file 10 MB.
                      Railway Careers automatically optimises the image.
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-cream/40 p-5">
                <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-ink">
                  Hero image
                </div>

                <p className="text-xs text-muted-foreground mt-1">
                  Optional wide image shown across the top of your public
                  company page.
                </p>

                <div className="mt-4">
                  <ImagePreview
                    src={
                      profile.profile_hero_image_url
                    }
                    type="hero"
                    companyName={
                      companyName
                    }
                  />
                </div>

                <input
                  id="company-hero-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(
                    event
                  ) =>
                    handleFile(
                      event,
                      "hero"
                    )
                  }
                />

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    className="bg-navy text-bone hover:bg-lime-600 hover:text-ink"
                    disabled={
                      saving ===
                      "hero"
                    }
                    onClick={() =>
                      document
                        .getElementById(
                          "company-hero-upload"
                        )
                        ?.click()
                    }
                  >
                    {saving ===
                    "hero" ? (
                      <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="mr-2 w-4 h-4" />
                    )}

                    {profile.profile_hero_image_url
                      ? "Replace hero image"
                      : "Upload hero image"}
                  </Button>

                  {profile.profile_hero_image_url && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={
                        saving ===
                        "hero"
                      }
                      onClick={() =>
                        clearImage(
                          "hero"
                        )
                      }
                    >
                      <Trash2 className="mr-2 w-3.5 h-3.5" />
                      Remove
                    </Button>
                  )}
                </div>

                <div className="text-[11px] text-muted-foreground mt-3">
                  A wide landscape photograph works best. It automatically
                  crops to suit different screen sizes.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}